import React, { memo, useCallback } from 'react';

import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import {
  actionSlotStyles,
  avatarColumnStyles,
  badgeStyles,
  bodyColumnStyles,
  channelNameStyles,
  getAccentStripeStyles,
  getPreviewTextStyles,
  getRootStyles,
  headerRowStyles,
  loanChipStyles,
  metaRowStyles,
  previewRowStyles,
  senderStyles,
  timestampStyles,
  trailingColumnStyles,
  typingLabelStyles,
} from './HeaderChatInboxRow.styles';

const HeaderChatInboxRow = memo(
  ({
    avatarNode = null,
    channelName,
    channelNameNode = null,
    senderName = null,
    preview = null,
    timestamp = null,
    loanLabel = null,
    typingLabel = null,
    unread = false,
    unreadCount = 0,
    actionSlot = null,
    dataChatId = null,
    dataMessageId = null,
    onSelect,
  }) => {
    const onRowClick = useCallback(() => {
      onSelect({ chatId: dataChatId, messageId: dataMessageId });
    }, [onSelect, dataChatId, dataMessageId]);

    return (
      <Box
        data-chat-id={dataChatId ?? ''}
        data-message-id={dataMessageId ?? ''}
        role="button"
        sx={getRootStyles({ unread })}
        tabIndex={0}
        onClick={onRowClick}
      >
        <Box sx={getAccentStripeStyles({ unread })} />
        {!!avatarNode && <Box sx={avatarColumnStyles}>{avatarNode}</Box>}
        <Box sx={bodyColumnStyles}>
          <Box sx={headerRowStyles}>
            {channelNameNode ?? (
              <Typography sx={channelNameStyles} variant={unread ? 'bold14' : 'medium14'}>
                {channelName}
              </Typography>
            )}
            <Box sx={metaRowStyles}>
              {!!loanLabel && (
                <Typography sx={loanChipStyles} variant="medium12">
                  {loanLabel}
                </Typography>
              )}
              {!!timestamp && (
                <Typography sx={timestampStyles} variant="regular12">
                  {timestamp}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={previewRowStyles}>
            {typingLabel ? (
              <Typography sx={typingLabelStyles} variant="regular12">
                {typingLabel}
              </Typography>
            ) : (
              <>
                {!!senderName && (
                  <Typography sx={senderStyles} variant="medium12">
                    {senderName}
                  </Typography>
                )}
                {!!preview && (
                  <Typography
                    sx={getPreviewTextStyles({ unread })}
                    variant={unread ? 'medium12' : 'regular12'}
                  >
                    {preview}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
        <Box sx={trailingColumnStyles}>
          {unreadCount > 0 && (
            <Box sx={badgeStyles}>
              <Typography variant="medium12">{unreadCount}</Typography>
            </Box>
          )}
          {!!actionSlot && <Box sx={actionSlotStyles}>{actionSlot}</Box>}
        </Box>
      </Box>
    );
  },
);

HeaderChatInboxRow.propTypes = {
  channelName: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  avatarNode: PropTypes.node,
  channelNameNode: PropTypes.node,
  senderName: PropTypes.string,
  preview: PropTypes.string,
  timestamp: PropTypes.string,
  loanLabel: PropTypes.string,
  typingLabel: PropTypes.string,
  unread: PropTypes.bool,
  unreadCount: PropTypes.number,
  actionSlot: PropTypes.node,
  dataChatId: PropTypes.string,
  dataMessageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default HeaderChatInboxRow;
